import React from "react";
import PropTypes from "prop-types";
import { getVarsoUrl } from "../../../../../common/helpers/helpers";
import GeneWithRefGenomeLink from "../../../../../common/helpers/GeneWithRefGenomeLink";
import {
  allCodingLocationsNull,
  allSpliceDistancesNull,
  calculateLocationBrackets,
} from "../TranscriptsTemplate";
import {
  includeManePlus,
  includeManeSelect,
  includeAppris,
  includeUniprotId,
  isProteinLevelAvailable,
  includeTSL,
  showGeneColumn,
} from "../helpers";
import ProteinLevel from "../ProteinLevel";
import { calculateColor, calculateTSL } from "./helpers";
import { primaryColor1 } from "../../../../../common/helpers/colors";
import Hgvs from "../../Hgvs/Hgvs";

const EnsemblTranscriptsBoxes = ({
  variantResult,
  transcripts,
  varsomeHost,
  genomeName,
  genomeSubdomain,
  properties,
  isFLT3ITD,
}) => {
  return (
    <div>
      {transcripts.map((item, i) => {
        const transcriptUrl = `${varsomeHost}/transcript/${genomeName}/${item.name}`;
        const ensemblUrl = `http://${genomeSubdomain}.ensembl.org/Homo_sapiens/Transcript/Summary?t=${item.name}`;
        const geneLink = `${varsomeHost}/gene/${item.gene_symbol}`;
        const uniprotUrl = `https://www.uniprot.org/uniprotkb/${item.uniprot_id}/entry`;
        const tslStyle = calculateColor(item);
        const hgvs = <Hgvs isFLT3ITD={isFLT3ITD} hgvs={item.hgvs} />;

        return (
          <dl className="dl-horizontal varsome-dl-horizontal" key={i}>
            <dt className="col-12 col-xs-12">{properties.name.label}</dt>
            <dd className="col-12 col-xs-12">
              <a href={transcriptUrl}>{item.name}</a>
              <br />
              <a href={getVarsoUrl(ensemblUrl)} target="_blank">
                <small style={{ color: primaryColor1 }}>See on Ensembl</small>
              </a>
            </dd>
            {item?.coding_impact && (
              <>
                <dt className="col-12 col-xs-12">
                  {properties.coding_impact.label}
                </dt>
                <dd className="col-12 col-xs-12">{item.coding_impact}</dd>
              </>
            )}
            {showGeneColumn(variantResult) && item.gene_symbol && (
              <div>
                <dt className="col-12 col-xs-12">Gene</dt>
                <dd className="col-12 col-xs-12">
                  {item.gene_symbol && (
                    <GeneWithRefGenomeLink href={geneLink}>
                      {item.gene_symbol}
                    </GeneWithRefGenomeLink>
                  )}
                </dd>
              </div>
            )}
            {item?.hgvs && (
              <>
                <dt className="col-12 col-xs-12">{properties.hgvs.label}</dt>
                <dd className="col-12 col-xs-12">{hgvs}</dd>
              </>
            )}
            {isProteinLevelAvailable(transcripts) && (
              <>
                <dt className="col-12 col-xs-12">HGVS Protein</dt>
                <dd className="col-12 col-xs-12">
                  <ProteinLevel isFLT3ITD={isFLT3ITD} item={item} />
                </dd>
              </>
            )}
            {item?.location && (
              <>
                <dt className="col-12 col-xs-12">
                  {properties.location.label}
                </dt>
                <dd className="col-12 col-xs-12">
                  {item.location + calculateLocationBrackets(item)}
                </dd>
              </>
            )}
            {!allCodingLocationsNull(transcripts) && item.coding_location && (
              <>
                <dt className="col-12 col-xs-12">
                  {properties.coding_location.label}
                </dt>
                <dd className="col-12 col-xs-12">{item.coding_location}</dd>
              </>
            )}
            {!allSpliceDistancesNull(transcripts) && item?.splice_distance && (
              <>
                <dt className="col-12 col-xs-12">
                  {properties.splice_distance.label}
                </dt>
                <dd className="col-12 col-xs-12">{item.splice_distance}</dd>
              </>
            )}
            {includeTSL(transcripts) && item.ensembl_support_level && (
              <>
                <dt className="col-12 col-xs-12">
                  {properties.ensembl_support_level.label}
                </dt>
                <dd style={tslStyle} className="col-12 col-xs-12">
                  {calculateTSL(item)}
                </dd>
              </>
            )}
            {includeManePlus(transcripts) && item?.mane_plus && (
              <>
                <dt className="col-12 col-xs-12">
                  {properties.mane_plus.label}
                </dt>
                <dd className="col-12 col-xs-12">{item.mane_plus}</dd>
              </>
            )}
            {includeManeSelect(transcripts) && item?.mane_select && (
              <>
                <dt className="col-12 col-xs-12">
                  {properties.mane_select.label}
                </dt>
                <dd className="col-12 col-xs-12">{item.mane_select}</dd>
              </>
            )}
            {includeAppris(transcripts) && item?.ensembl_appris && (
              <>
                <dt className="col-12 col-xs-12">
                  {properties.ensembl_appris.label}
                </dt>
                <dd className="col-12 col-xs-12">{item.ensembl_appris}</dd>
              </>
            )}
            {includeUniprotId(transcripts) && item.uniprot_id && (
              <>
                <dt className="col-12 col-xs-12">
                  {properties.uniprot_id.label}
                </dt>
                <dd className="col-12 col-xs-12">
                  {item?.uniprot_id && (
                    <a href={uniprotUrl} target="_blank">
                      {item.uniprot_id}
                    </a>
                  )}
                </dd>
              </>
            )}
          </dl>
        );
      })}
    </div>
  );
};

EnsemblTranscriptsBoxes.propTypes = {
  variantResult: PropTypes.shape({}),
  transcripts: PropTypes.arrayOf(PropTypes.shape({})),
  varsomeHost: PropTypes.string,
  genomeName: PropTypes.string,
  genomeSubdomain: PropTypes.string,
  properties: PropTypes.shape({
    name: PropTypes.shape({
      label: PropTypes.string,
    }),
    strand: PropTypes.shape({
      label: PropTypes.string,
    }),
    coding_impact: PropTypes.shape({
      label: PropTypes.string,
    }),
    hgvs: PropTypes.shape({
      label: PropTypes.string,
    }),
    hgvs_p1: PropTypes.shape({
      label: PropTypes.string,
    }),
    hgvs_p3: PropTypes.shape({
      label: PropTypes.string,
    }),
    location: PropTypes.shape({
      label: PropTypes.string,
    }),
    coding_location: PropTypes.shape({
      label: PropTypes.string,
    }),
    canonical: PropTypes.shape({
      label: PropTypes.string,
    }),
    gene_symbol: PropTypes.shape({
      label: PropTypes.string,
    }),
    splice_distance: PropTypes.shape({
      label: PropTypes.string,
    }),
    ensembl_support_level: PropTypes.shape({
      label: PropTypes.string,
    }),
    ensembl_appris: PropTypes.shape({
      label: PropTypes.string,
    }),
    mane_select: PropTypes.shape({
      label: PropTypes.string,
    }),
    mane_plus: PropTypes.shape({
      label: PropTypes.string,
    }),
    uniprot_id: PropTypes.shape({
      label: PropTypes.string,
    }),
  }),
  isFLT3ITD: PropTypes.bool,
};

EnsemblTranscriptsBoxes.defaultProps = {
  variantResult: null,
  transcripts: [],
  varsomeHost: null,
  genomeName: null,
  genomeSubdomain: null,
  properties: {},
  isFLT3ITD: false,
};
export default EnsemblTranscriptsBoxes;
